import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** Chiavi delle query, in un solo posto: le mutazioni invalidano da qui. */
export const keys = {
  clients: ["clients"] as const,
  client: (id: string) => ["clients", id] as const,
  accounts: ["accounts"] as const,
  connectedCount: ["accounts", "connected"] as const,
  servers: ["servers"] as const,
  propFirms: ["prop-firms"] as const,
  sessions: ["sessions"] as const,
  cycles: ["cycles"] as const,
  cycleList: (q: object) => ["cycles", "list", q] as const,
  cycle: (id: string) => ["cycles", "detail", id] as const,
  sessionList: (q: object) => ["sessions", "list", q] as const,
  session: (id: string) => ["sessions", "detail", id] as const,
  mirrorHealth: ["mirror-health"] as const,
  ledger: (clientId: string) => ["ledger", clientId] as const,
  reconciliation: (accountId: string) => ["reconciliation", accountId] as const,
  riskOpen: (q: object) => ["risk", "open", q] as const,
  riskList: (q: object) => ["risk", "list", q] as const,
  audit: (q: object) => ["audit", q] as const,
};

export const useClients = () => useQuery({ queryKey: keys.clients, queryFn: api.clients.list });
export const useClient = (id: string) => useQuery({ queryKey: keys.client(id), queryFn: () => api.clients.get(id) });
export const useAccountGroups = () => useQuery({ queryKey: keys.accounts, queryFn: api.accounts.list });
/** Conti connessi adesso: sta sotto "accounts", quindi si rinfresca con loro. */
export const useConnectedCount = () => useQuery({ queryKey: keys.connectedCount, queryFn: api.accounts.connectedCount });
export const useServers = () => useQuery({ queryKey: keys.servers, queryFn: api.servers.list });
export const usePropFirms = () => useQuery({ queryKey: keys.propFirms, queryFn: api.propFirms.list });

export const useCycles = (q: { state?: string[]; clientId?: string } = {}) =>
  useQuery({ queryKey: keys.cycleList(q), queryFn: () => api.cycles.list(q) });
export const useCycle = (id: string) => useQuery({ queryKey: keys.cycle(id), queryFn: () => api.cycles.get(id) });
/**
 * I periodi del ciclo. Sotto la chiave del ciclo: una base corretta, un payout
 * registrato o una chiusura di fase li rinfrescano insieme ai suoi numeri.
 *
 * `enabled` perché la rotta non è nell'allowlist di un CLIENT: la sezione non
 * si mostra a lui, e la chiamata non deve partire.
 */
export const useCyclePeriods = (id: string, enabled = true) =>
  useQuery({ queryKey: [...keys.cycle(id), "periods"], queryFn: () => api.cycles.periods(id), enabled });

export const useSessions = (q: { state?: string[]; clientId?: string; phase?: string }) =>
  useQuery({ queryKey: keys.sessionList(q), queryFn: () => api.sessions.list(q) });
export const useSession = (id: string) =>
  useQuery({
    queryKey: keys.session(id),
    queryFn: () => api.sessions.get(id),
    // Con coppie aperte il P&L live si rinfresca da solo.
    refetchInterval: (query) => ((query.state.data?.summary.openPairs ?? 0) > 0 ? 5_000 : false),
  });

/**
 * I simboli su cui una fase puo' operare. Sotto "sessions": una sincronizzazione
 * che sblocca un simbolo, o una mappatura tolta, la rinfresca con la fase.
 */
export const useSessionSymbols = (id: string) =>
  useQuery({ queryKey: [...keys.sessions, "symbols", id], queryFn: () => api.sessions.symbols(id) });

/**
 * Che cosa e' rotto ADESSO. Si rinfresca da solo: e' la lista che si guarda
 * mentre si opera, e un allarme nato due minuti fa non deve aspettare un F5.
 */
export const useOpenRiskEvents = (q: { sessionId?: string; accountId?: string } = {}, enabled = true) =>
  useQuery({ queryKey: keys.riskOpen(q), queryFn: () => api.risk.open(q), refetchInterval: 30_000, enabled });

export const useRiskEvents = (q: Parameters<typeof api.risk.list>[0]) =>
  useQuery({ queryKey: keys.riskList(q ?? {}), queryFn: () => api.risk.list(q) });

export const useAudit = (q: Parameters<typeof api.audit>[0]) =>
  useQuery({ queryKey: keys.audit(q ?? {}), queryFn: () => api.audit(q) });

/**
 * Lo stato delle guardie di rischio, ogni minuto.
 *
 * Non e' un dettaglio da pagina di stato: con l'intervallo a zero non gira
 * NIENTE — gamba sparita, DD giornaliero, conti disconnessi, proxy — e in
 * produzione e' stato cosi' per giorni senza che si vedesse da nessuna parte.
 */
export const useRiskChecksHealth = (enabled = true) =>
  useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 60_000, enabled });

/** Il client Mirror finto abilita i comandi di simulazione in sviluppo. */
export const useMirrorIsFake = () => {
  const q = useQuery({ queryKey: keys.mirrorHealth, queryFn: api.dev.mirrorHealth, staleTime: Infinity });
  return !!q.data?.version?.startsWith("fake");
};

/**
 * Dopo una modifica a clienti, conti, proxy o sessioni si rinfresca tutto ciò
 * che li mostra: le stesse informazioni compaiono in più pagine.
 */
export function useRefreshDirectory() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.clients }),
      qc.invalidateQueries({ queryKey: keys.accounts }),
      qc.invalidateQueries({ queryKey: keys.servers }),
      qc.invalidateQueries({ queryKey: keys.sessions }),
      qc.invalidateQueries({ queryKey: keys.cycles }),
    ]);
}

/** Valore che si aggiorna solo dopo `ms` di quiete: evita una preview a ogni tasto. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
